// src/pages/Fellows.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Star,
  Flame,
  CheckCircle2,
  Clock,
  Lock,
  ChevronDown,
  ChevronUp,
  MapPin,
  Calendar,
  BookOpen,
  ArrowRight,
  Award,
  Users,
  Zap,
  Quote,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  useGetMyFellowsStatusQuery,
  useGetPublishedFellowsQuery,
  type FellowRecord,
  type EligibilityCriterion,
} from "../../app/api/fellowsApi";
import {
  useGetMyQuarterQuery,
  type QuarterKey,
} from "../../app/api/programmeApi";
import { useGetJourneyStatsQuery } from "../../app/api/journeyApi";
import type { MemberTier } from "../../app/api/authApi";

/* ─────────────────────────────────────────────
   STATIC EXPLANATORY DATA
───────────────────────────────────────────── */

interface SelectionStep {
  step: number;
  title: string;
  description: string;
  timing: string;
}

const SELECTION_STEPS: SelectionStep[] = [
  {
    step: 1,
    title: "Holistic assessment",
    description:
      "The Organizing Committee reviews task scores, engagement metrics, library activity, and event attendance for active members at the close of each quarter.",
    timing: "Day 80–88 of the 90-day cycle",
  },
  {
    step: 2,
    title: "Regional Representative input",
    description:
      "Regional Reps submit nominations with commentary on members who show strong growth, local leadership, and consistency.",
    timing: "Day 85–89",
  },
  {
    step: 3,
    title: "Committee shortlist",
    description:
      "The Committee Chair and leadership team review nominations and finalise a cohort of exactly 10 Fellows for that quarter.",
    timing: "Day 89–90",
  },
  {
    step: 4,
    title: "Announcement & onboarding",
    description:
      "Selected Fellows are announced at the quarterly event and contacted with shadowing logistics and expectations.",
    timing: "Day 90 — Event day",
  },
  {
    step: 5,
    title: "One-week shadowing",
    description:
      "Selected Fellows shadow the Continental Overseer for one week after the event and later submit a written report for publication.",
    timing: "Week following Day 90",
  },
];

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
    {children}
  </h2>
);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const prettyQuarter = (q?: string | null) => q ?? "—";

const prettyTier = (tier?: string | null): string => {
  if (!tier) return "Member";
  const map: Record<string, string> = {
    wider_audience: "Wider Audience",
    inner_circle: "Inner Circle",
    legacy_fellows: "Legacy Fellows",
  };
  return map[tier] ?? tier;
};

const prettyCriterionLabel = (label: string) => label;

const prettyStatus = (status?: string | null) => {
  if (!status) return "Pending";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getInitials = (fellow: FellowRecord) => {
  const first = fellow.mentee.firstName?.[0] ?? "";
  const last = fellow.mentee.lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "F";
};

const avatarColourClasses = [
  "bg-blue-500",
  "bg-rose-500",
  "bg-green-600",
  "bg-purple-500",
  "bg-amber-500",
  "bg-cyan-500",
  "bg-indigo-500",
  "bg-pink-500",
];

const colourFromString = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return avatarColourClasses[Math.abs(hash) % avatarColourClasses.length];
};

const getNextQuarter = (q?: QuarterKey | null): QuarterKey | null => {
  if (!q) return null;
  const order: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];
  const idx = order.indexOf(q);
  return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
};

/* ─────────────────────────────────────────────
   SUB COMPONENTS
───────────────────────────────────────────── */

const FellowCard: React.FC<{ fellow: FellowRecord }> = ({ fellow }) => {
  const [expanded, setExpanded] = useState(false);

  const fullName = `${fellow.mentee.firstName} ${fellow.mentee.lastName}`;
  const reportTitle = fellow.report?.title || "Post-shadowing report";
  const reportExcerpt =
    fellow.report?.excerpt ||
    fellow.report?.body ||
    "No report excerpt available yet.";
  const keyLesson =
    fellow.report?.keyLesson || "No key lesson was provided for this report.";
  const reportDate = fellow.report?.publishedAt || fellow.updatedAt;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-200 hover:border-gray-200 hover:shadow-md">
      <div className="p-5">
        <div className="flex items-start gap-4">
          {fellow.mentee.avatar ? (
            <img
              src={fellow.mentee.avatar}
              alt={fullName}
              className="shrink-0 w-11 h-11 rounded-xl object-cover"
            />
          ) : (
            <div
              className={`shrink-0 w-11 h-11 rounded-xl ${colourFromString(fullName)} flex items-center justify-center text-white text-sm font-bold`}
            >
              {getInitials(fellow)}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {fullName}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {fellow.mentee.ministryRole || "GIMLP Member"}
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                <Star size={9} /> {fellow.quarter} Fellow
              </span>
            </div>
            <div className="flex items-center gap-1 mt-1.5 text-[11px] text-gray-400">
              <MapPin size={10} />
              <span>{fellow.mentee.region || "Region not specified"}</span>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-50">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
            Post-shadowing report
          </p>
          <h4 className="text-sm font-semibold text-gray-800 leading-snug">
            {reportTitle}
          </h4>
          <p className="text-[11px] text-gray-400 mt-1">
            {fmtDate(reportDate)}
          </p>
        </div>

        <div className="mt-3 relative">
          <div
            className={`text-xs text-gray-600 leading-relaxed ${
              !expanded ? "line-clamp-3" : ""
            }`}
          >
            <Quote size={12} className="inline text-gray-300 mr-1 -mt-0.5" />
            {reportExcerpt}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mx-5 mb-5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 mb-1.5">
            Key lesson
          </p>
          <p className="text-xs text-amber-900 leading-relaxed italic">
            "{keyLesson}"
          </p>

          {(fellow.shadowingStartDate || fellow.shadowingEndDate) && (
            <p className="text-[11px] text-amber-700 mt-3">
              Shadowing: {fmtDate(fellow.shadowingStartDate)} —{" "}
              {fmtDate(fellow.shadowingEndDate)}
              {fellow.shadowingLocation ? ` · ${fellow.shadowingLocation}` : ""}
            </p>
          )}
        </div>
      )}

      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-center gap-1.5 py-3 border-t border-gray-50 text-xs font-semibold text-green-600 hover:text-green-700 hover:bg-green-50 transition-colors"
      >
        {expanded ? (
          <>
            <ChevronUp size={13} /> Show less
          </>
        ) : (
          <>
            <ChevronDown size={13} /> Read full report & key lesson
          </>
        )}
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */

const Fellows: React.FC = () => {
  const [selectionOpen, setSelectionOpen] = useState(false);

  const {
    data: myStatusRes,
    isLoading: statusLoading,
    isError: statusError,
  } = useGetMyFellowsStatusQuery();

  const {
    data: publishedRes,
    isLoading: publishedLoading,
    isError: publishedError,
  } = useGetPublishedFellowsQuery({});

  const { data: myQuarterRes, isLoading: quarterLoading } =
    useGetMyQuarterQuery();
  const { data: journeyStatsRes, isLoading: statsLoading } =
    useGetJourneyStatsQuery();

  const myStatus = myStatusRes?.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const published = publishedRes?.data ?? [];
  const myQuarter = myQuarterRes?.data;
  const journeyStats = journeyStatsRes?.data;

  const isLoading =
    statusLoading || publishedLoading || quarterLoading || statsLoading;

  const eligible = myStatus?.eligible ?? false;
  const targetQuarter =
    myStatus?.eligibleQuarter ??
    getNextQuarter(myQuarter?.currentQuarter) ??
    myQuarter?.currentQuarter ??
    "Q1";

  const metCount = myStatus?.metCount ?? 0;
  const totalCount = myStatus?.totalCriteria ?? 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const criteria = myStatus?.criteria ?? [];
  const eligibilityPct =
    totalCount > 0 ? Math.round((metCount / totalCount) * 100) : 0;

  const publishedQuarterOptions = useMemo(() => {
    const quarters = Array.from(new Set(published.map((f) => f.quarter)));
    const ordered: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];
    return ordered.filter((q) => quarters.includes(q));
  }, [published]);

  const defaultPublishedQuarter =
    publishedQuarterOptions[0] ?? (targetQuarter as QuarterKey);

  const [selectedPublishedQuarter, setSelectedPublishedQuarter] =
    useState<QuarterKey>(defaultPublishedQuarter);

  useEffect(() => {
    if (
      publishedQuarterOptions.length > 0 &&
      !publishedQuarterOptions.includes(selectedPublishedQuarter)
    ) {
      setSelectedPublishedQuarter(publishedQuarterOptions[0]);
    }
  }, [publishedQuarterOptions, selectedPublishedQuarter]);

  const visiblePublishedFellows = useMemo(
    () =>
      published.filter((fellow) => fellow.quarter === selectedPublishedQuarter),
    [published, selectedPublishedQuarter],
  );

  const nextSelectionQuarter =
    getNextQuarter(myQuarter?.currentQuarter) ??
    myStatus?.eligibleQuarter ??
    targetQuarter;

  const heroStats = useMemo(() => {
    const totalPublishedFellows = published.length;
    const publishedQuarterCount = publishedQuarterOptions.length;
    const totalShadowingDays = published.filter(
      (f) => f.shadowingStartDate || f.shadowingEndDate,
    ).length
      ? 7
      : 7;

    return {
      fellowsPerQuarter: "10",
      quarterCount: String(Math.max(publishedQuarterCount, 1)),
      shadowingDays: String(totalShadowingDays),
      nextSelection: prettyQuarter(nextSelectionQuarter),
      totalPublishedFellows,
    };
  }, [published, publishedQuarterOptions.length, nextSelectionQuarter]);

  const currentTier = prettyTier(
    (myStatus?.currentTier as MemberTier | undefined) ??
      (journeyStats?.currentTier as MemberTier | undefined),
  );

  const dynamicCriteria = useMemo(() => {
    return criteria.map((c: EligibilityCriterion) => {
      const labelLower = c.label.toLowerCase();

      let description = "";
      if (labelLower.includes("task completion")) {
        description =
          "Submit leadership tasks consistently across quarters. Strong completion history is one of the clearest signals used in Fellows consideration.";
      } else if (labelLower.includes("task quality")) {
        description =
          "Maintain reflective, high-quality submissions. Strong scores show maturity, depth, and seriousness.";
      } else if (labelLower.includes("library")) {
        description =
          "Engage actively with recordings, reading lists, and resources each quarter to show learning momentum.";
      } else if (labelLower.includes("event attendance")) {
        description =
          "Attend quarterly events consistently, whether physically or through approved live participation.";
      } else if (
        labelLower.includes("regional") ||
        labelLower.includes("nomination")
      ) {
        description =
          "Regional visibility matters. Final consideration depends on leadership observation and nomination patterns.";
      } else {
        description =
          "This criterion contributes to overall Fellows consideration for the quarter.";
      }

      let icon: React.ReactNode = <Star size={15} />;
      if (labelLower.includes("task completion")) {
        icon = <CheckCircle2 size={15} />;
      } else if (labelLower.includes("library")) {
        icon = <BookOpen size={15} />;
      } else if (labelLower.includes("event")) {
        icon = <Calendar size={15} />;
      } else if (
        labelLower.includes("regional") ||
        labelLower.includes("nomination")
      ) {
        icon = <MapPin size={15} />;
      } else if (
        labelLower.includes("quality") ||
        labelLower.includes("score")
      ) {
        icon = <Star size={15} />;
      } else if (!c.met) {
        icon = <Clock size={15} />;
      }

      return {
        ...c,
        description,
        icon,
      };
    });
  }, [criteria]);

  if (isLoading) {
    return (
      <div className="space-y-8 pb-12">
        <div className="space-y-2">
          <Bone className="h-3 w-28" />
          <Bone className="h-8 w-48" />
          <Bone className="h-4 w-80" />
        </div>
        <Bone className="h-64 w-full rounded-2xl" />
        <Bone className="h-72 w-full rounded-2xl" />
        <Bone className="h-20 w-full rounded-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <Bone key={i} className="h-48" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Bone key={i} className="h-72" />
          ))}
        </div>
      </div>
    );
  }

  if (statusError || publishedError) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
          <AlertCircle size={20} className="text-red-400" />
        </div>
        <p className="text-sm font-semibold text-gray-700">
          Unable to load fellows data
        </p>
        <p className="text-xs text-gray-400 max-w-xs">
          Check your connection and refresh the page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* PAGE HEADER */}
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-amber-600 mb-1">
          GIMLP Programme
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
          Legacy Fellows
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          The most intensive tier of the programme — 10 members selected each
          quarter for a one-week in-person shadowing experience with the
          Continental Overseer.
        </p>
      </div>

      {/* HERO BANNER */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-700 via-amber-800 to-amber-950 text-white p-6 md:p-8 shadow-lg">
        <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -right-2 -top-2 h-32 w-32 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute left-1/2 bottom-0 h-24 w-24 rounded-full border border-white/5" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Star size={20} className="text-amber-200" />
              </div>
              <span className="text-white/70 text-xs font-medium uppercase tracking-widest">
                Rotating cohort · 10 per quarter
              </span>
            </div>
            <h2 className="text-xl font-bold leading-snug mb-2">
              Direct exposure to apostolic leadership
            </h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-md">
              Legacy Fellows shadow the Continental Overseer in person for one
              week — observing governance decisions, ministry operations, and
              the disciplines that sustain Kingdom leadership at scale.
            </p>
          </div>

          <div className="shrink-0 grid grid-cols-2 gap-3">
            {[
              {
                value: heroStats.fellowsPerQuarter,
                label: "Fellows per quarter",
              },
              {
                value: heroStats.quarterCount,
                label: "Published cohorts",
              },
              {
                value: heroStats.shadowingDays,
                label: "Days shadowing",
              },
              {
                value: heroStats.nextSelection,
                label: "Next selection",
              },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl bg-white/10 backdrop-blur-sm px-4 py-3 text-center"
              >
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-[11px] text-white/60 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* YOUR ELIGIBILITY */}
      <div>
        <SectionHeading>
          Your eligibility — {prettyQuarter(targetQuarter)} selection
        </SectionHeading>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {eligible ? (
                  <Flame size={16} className="text-amber-500" />
                ) : (
                  <Lock size={16} className="text-gray-400" />
                )}
                <h3 className="text-sm font-semibold text-gray-900">
                  {eligible
                    ? `You are on track for consideration in ${prettyQuarter(
                        targetQuarter,
                      )}`
                    : "Keep building toward eligibility"}
                </h3>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                {eligible
                  ? `Your current tier is ${currentTier}. Continue strengthening your record through task completion, event attendance, and library engagement. Final selection is made by the Organizing Committee.`
                  : `You are not yet flagged as eligible. Work through the criteria below and keep building a strong quarter-by-quarter record.`}
              </p>
            </div>

            <div className="shrink-0 flex flex-col items-center gap-1">
              <div className="relative w-16 h-16">
                <svg
                  width="64"
                  height="64"
                  className="-rotate-90"
                  style={{ position: "absolute" }}
                >
                  <circle
                    cx="32"
                    cy="32"
                    r="26"
                    fill="none"
                    stroke="#fef3c7"
                    strokeWidth="5"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="26"
                    fill="none"
                    stroke="#d97706"
                    strokeWidth="5"
                    strokeDasharray={`${
                      (eligibilityPct / 100) * 2 * Math.PI * 26
                    } ${2 * Math.PI * 26}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-sm font-bold text-amber-700">
                    {metCount}/{totalCount}
                  </span>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 font-medium">
                criteria met
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {dynamicCriteria.length === 0 ? (
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-4 text-sm text-gray-500">
                No eligibility criteria available yet.
              </div>
            ) : (
              dynamicCriteria.map((c, i) => (
                <div
                  key={c.key || i}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-colors
                    ${
                      c.met
                        ? "bg-green-50 border-green-100"
                        : "bg-gray-50 border-gray-100"
                    }`}
                >
                  <div
                    className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5
                      ${
                        c.met
                          ? "bg-green-500 text-white"
                          : "bg-gray-200 text-gray-400"
                      }`}
                  >
                    {c.met ? <CheckCircle2 size={13} /> : c.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-xs font-semibold leading-snug
                        ${c.met ? "text-green-800" : "text-gray-700"}`}
                    >
                      {prettyCriterionLabel(c.label)}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">
                      {c.description}
                    </p>
                    {c.value && (
                      <p
                        className={`text-[11px] font-medium mt-1
                          ${c.met ? "text-green-600" : "text-amber-600"}`}
                      >
                        {c.value}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {myStatus?.nominations?.length ? (
            <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 mb-1.5">
                Your nomination history
              </p>
              <div className="space-y-1.5">
                {myStatus.nominations.map((n, i) => (
                  <p
                    key={`${n.quarter}-${i}`}
                    className="text-xs text-amber-900"
                  >
                    {n.quarter} · {prettyStatus(n.status)} · Nominated{" "}
                    {fmtDate(n.nominatedAt)}
                    {n.reportPublished ? " · Report published" : ""}
                  </p>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Link
              to="/user/tasks"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 active:scale-95 transition-all shadow-sm"
            >
              Complete pending tasks <ArrowRight size={14} />
            </Link>
            <Link
              to="/user/journey"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all"
            >
              View my full journey
            </Link>
          </div>
        </div>
      </div>

      {/* HOW SELECTION WORKS */}
      <div>
        <button
          onClick={() => setSelectionOpen(!selectionOpen)}
          className="w-full flex items-center justify-between bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 hover:border-gray-200 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Zap size={15} className="text-amber-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-gray-900">
                How the selection process works
              </h3>
              <p className="text-xs text-gray-400">
                5-step process · No direct application
              </p>
            </div>
          </div>
          {selectionOpen ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </button>

        {selectionOpen && (
          <div className="mt-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="relative">
              <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gray-100" />
              <div className="space-y-1">
                {SELECTION_STEPS.map((step, idx) => (
                  <div
                    key={step.step}
                    className="relative flex gap-4 pb-5 last:pb-0"
                  >
                    <div
                      className={`shrink-0 z-10 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2
                        ${
                          idx === SELECTION_STEPS.length - 1
                            ? "bg-amber-500 border-amber-500 text-white"
                            : "bg-white border-gray-200 text-gray-600"
                        }`}
                    >
                      {step.step}
                    </div>
                    <div className="flex-1 pt-2">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {step.title}
                        </h4>
                        <span className="shrink-0 text-[10px] font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">
                          {step.timing}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* WHAT TO EXPECT */}
      <div>
        <SectionHeading>What the shadowing week looks like</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              icon: <Users size={18} className="text-blue-600" />,
              bg: "bg-blue-50",
              title: "Observe, don't perform",
              body: "Fellows are selected to observe how leadership works at close range. The goal is formation through exposure, not public performance.",
            },
            {
              icon: <BookOpen size={18} className="text-green-600" />,
              bg: "bg-green-50",
              title: "Daily debrief sessions",
              body: "The shadowing week is designed to translate observation into practical lessons through reflection, note-taking, and guided interpretation.",
            },
            {
              icon: <Award size={18} className="text-amber-600" />,
              bg: "bg-amber-50",
              title: "Post-shadowing report",
              body: "Every selected Fellow is expected to submit a written report after the week. Published reports become part of the learning archive for the wider community.",
            },
          ].map((card) => (
            <div
              key={card.title}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
            >
              <div
                className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center mb-4`}
              >
                {card.icon}
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {card.title}
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                {card.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* PUBLISHED COHORTS & REPORTS */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <SectionHeading>Published cohort reports</SectionHeading>
          <div className="flex items-center gap-1 flex-wrap">
            {publishedQuarterOptions.length > 0 ? (
              publishedQuarterOptions.map((q) => (
                <button
                  key={q}
                  onClick={() => setSelectedPublishedQuarter(q)}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors
                    ${
                      selectedPublishedQuarter === q
                        ? "bg-amber-600 text-white"
                        : "bg-gray-100 text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {q}
                </button>
              ))
            ) : (
              <span className="text-xs text-gray-400">
                No published cohorts yet
              </span>
            )}
          </div>
        </div>

        {visiblePublishedFellows.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-gray-500">
                {visiblePublishedFellows.length} fellow
                {visiblePublishedFellows.length !== 1 ? "s" : ""} published for{" "}
                {selectedPublishedQuarter}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visiblePublishedFellows.map((fellow) => (
                <FellowCard key={fellow.id} fellow={fellow} />
              ))}
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                No published reports yet
              </p>
              <h3 className="text-sm font-semibold text-gray-700">
                Reports for this cohort have not been published
              </h3>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                Once reports are published by the portal team, they will appear
                here automatically.
              </p>
            </div>
            <div className="shrink-0 w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
              <Loader2 size={20} className="text-gray-300" />
            </div>
          </div>
        )}
      </div>

      {/* FUTURE COHORTS PLACEHOLDER */}
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
            Coming next
          </p>
          <h3 className="text-sm font-semibold text-gray-700">
            {prettyQuarter(nextSelectionQuarter)} Fellows cohort
          </h3>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">
            The next Fellows cohort will appear here after selection and report
            publication. Published reports are loaded dynamically from the
            portal.
          </p>
        </div>
        <div className="shrink-0 w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
          <Clock size={20} className="text-gray-300" />
        </div>
      </div>
    </div>
  );
};

export default Fellows;
